import { useState, useEffect } from 'react';
import UserService from '../services/user.service';
import { useAuth } from '../context/AuthContext';
import { User, MapPin, Shield, Plus, Edit2, Trash2, Bell } from 'lucide-react';
import NotificationService from '../services/notification.service';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/Button';
import { InputField } from '../components/ui/InputField';

const UserProfilePage = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);

    // Profile State
    const [profileData, setProfileData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        email: ''
    });

    const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
    const [notifications, setNotifications] = useState([]);

    // Address State
    const [addresses, setAddresses] = useState([]);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [addressForm, setAddressForm] = useState({
        id: null,
        address_type: 'shipping',
        gps_address: '',
        street_address: '',
        area: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'Ghana',
        latitude: null,
        longitude: null,
        contact_name: '',
        contact_phone: ''
    });

    const [fetchingGps, setFetchingGps] = useState(false);

    const fetchDetailsFromGps = async () => {
        if (!addressForm.gps_address) {
            toast.error('Please enter a GhanaPostGPS address first.');
            return;
        }
        setFetchingGps(true);
        try {
            const formData = new URLSearchParams();
            formData.append('address', addressForm.gps_address);
            
            const response = await fetch('https://ghanapostgps.sperixlabs.org/get-location', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formData.toString()
            });
            const result = await response.json();
            
            if (result.found && result.data?.Table?.length > 0) {
                const data = result.data.Table[0];
                setAddressForm(prev => ({
                    ...prev,
                    street_address: data.Street && data.Street !== '[UNKNOWN]' ? data.Street : prev.street_address,
                    area: data.Area || prev.area,
                    city: data.District || prev.city,
                    state: data.Region || prev.state,
                    postal_code: data.PostCode || prev.postal_code,
                    latitude: data.CenterLatitude ? Number(data.CenterLatitude).toFixed(8) : prev.latitude,
                    longitude: data.CenterLongitude ? Number(data.CenterLongitude).toFixed(8) : prev.longitude,
                    country: 'Ghana'
                }));
                toast.success('Address details fetched successfully!');
            } else {
                toast.error('Could not find details for this GPS address.');
            }
        } catch (error) {
            toast.error('Error fetching GPS details.');
        } finally {
            setFetchingGps(false);
        }
    };

    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported by your browser.');
            return;
        }

        setFetchingGps(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const lat = position.coords.latitude;
                    const long = position.coords.longitude;
                    
                    const formData = new FormData();
                    formData.append('lat', lat.toString());
                    formData.append('long', long.toString());
                    
                    const response = await fetch('https://ghanapostgps.sperixlabs.org/get-address', {
                        method: 'POST',
                        body: formData
                    });
                    const result = await response.json();
                    
                    if (result.found && result.data?.Table?.length > 0) {
                        const data = result.data.Table[0];
                        setAddressForm(prev => ({
                            ...prev,
                            gps_address: data.GPSName || prev.gps_address,
                            street_address: data.Street && data.Street !== '[UNKNOWN]' ? data.Street : prev.street_address,
                            area: data.Area || prev.area,
                            city: data.District || prev.city,
                            state: data.Region || prev.state,
                            postal_code: data.PostCode || prev.postal_code,
                            latitude: Number(lat).toFixed(8),
                            longitude: Number(long).toFixed(8),
                            country: 'Ghana'
                        }));
                        toast.success('Location fetched successfully!');
                    } else {
                        toast.error('Could not determine GhanaPostGPS address for your location.');
                    }
                } catch (error) {
                    toast.error('Error resolving current location.');
                } finally {
                    setFetchingGps(false);
                }
            },
            (error) => {
                toast.error('Unable to retrieve your location. Please check browser permissions.');
                setFetchingGps(false);
            }
        );
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const userRes = await UserService.getProfile();
            setProfileData({
                id: userRes.data.id,
                first_name: userRes.data.first_name || '',
                last_name: userRes.data.last_name || '',
                phone: userRes.data.phone || '',
                email: userRes.data.email || ''
            });

            const addrRes = await UserService.getAddresses();
            setAddresses(addrRes.data.results || []);

            const notifRes = await NotificationService.getNotifications();
            setNotifications(notifRes.data.results || []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load profile data");
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        try {
            await UserService.updateProfile(profileData);
            toast.success("Profile updated successfully");
        } catch (error) {
            console.error(error);
            toast.error("Failed to update profile");
        }
    };

    const handleAddressSubmit = async (e) => {
        e.preventDefault();
        try {
            if (addressForm.id) {
                await UserService.updateAddress(addressForm.id, addressForm);
                toast.success("Address updated");
            } else {
                await UserService.addAddress(addressForm);
                toast.success("Address added");
            }
            setShowAddressModal(false);
            loadData();
            setAddressForm({
                id: null,
                address_type: 'shipping',
                gps_address: '',
                street_address: '',
                area: '',
                city: '',
                state: '',
                postal_code: '',
                country: 'Ghana',
                latitude: null,
                longitude: null,
                contact_name: '',
                contact_phone: ''
            });
        } catch (error) {
            console.error(error);
            toast.error("Failed to save address");
        }
    };

    const handleDeleteAddress = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            await UserService.deleteAddress(id);
            toast.success("Address deleted");
            setAddresses(addresses.filter(a => a.id !== id));
        } catch (error) {
            toast.error("Failed to delete");
        }
    };

    const handleSetDefault = async (id) => {
        try {
            await UserService.setDefaultAddress(id);
            toast.success("Set as default");
            loadData();
        } catch (error) {
            toast.error("Failed to set default");
        }
    };

    const openEditAddress = (addr) => {
        setAddressForm(addr);
        setShowAddressModal(true);
    };

    const renderProfileTab = () => (
        <form onSubmit={handleProfileUpdate} className="max-w-xl space-y-6">
            <div className="grid grid-cols-2 gap-6">
                <InputField label="First Name" value={profileData.first_name} onChange={e => setProfileData({ ...profileData, first_name: e.target.value })} />
                <InputField label="Last Name" value={profileData.last_name} onChange={e => setProfileData({ ...profileData, last_name: e.target.value })} />
            </div>
            <InputField label="Email" value={profileData.email} disabled />
            <InputField label="Phone Number" value={profileData.phone} onChange={e => setProfileData({ ...profileData, phone: e.target.value })} />
            <Button type="submit">Save Changes</Button>
        </form>
    );

    const renderAddressesTab = () => (
        <div>
            <div className="flex justify-between items-center mb-8">
                <h3 className="font-serif text-2xl">Address Book</h3>
                <Button
                    variant="secondary"
                    onClick={() => {
                        setAddressForm({
                            id: null, address_type: 'shipping', gps_address: '', street_address: '', area: '', city: '', state: '', postal_code: '', country: 'Ghana', latitude: null, longitude: null, contact_name: '', contact_phone: ''
                        });
                        setShowAddressModal(true);
                    }}
                    className="py-2 px-4 text-xs"
                >
                    <Plus size={14} className="mr-2" /> Add New
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {addresses.map((addr) => (
                    <div key={addr.id} className={`p-6 border relative transition-all group hover:border-black ${addr.is_default ? 'border-black bg-gray-50' : 'border-gray-200'}`}>
                        {addr.is_default && (
                            <span className="absolute top-4 right-4 text-[10px] font-bold bg-black text-white px-2 py-1 uppercase tracking-widest">Default</span>
                        )}
                        <div className="flex items-start gap-3 mb-4">
                            <MapPin className="text-gray-400 mt-1" size={16} />
                            <div className="text-sm">
                                <p className="font-bold text-black mb-1">{addr.contact_name}</p>
                                <p className="text-gray-600">{addr.street_address}</p>
                                <p className="text-gray-600">{addr.city}, {addr.state} {addr.postal_code}</p>
                                <p className="text-gray-600">{addr.country}</p>
                                <p className="text-gray-500 mt-2 font-sans text-xs">{addr.contact_phone}</p>
                            </div>
                        </div>
                        <div className="flex gap-4 pt-4 border-t border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!addr.is_default && (
                                <button onClick={() => handleSetDefault(addr.id)} className="text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-black">Default</button>
                            )}
                            <button onClick={() => openEditAddress(addr)} className="text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-black ml-auto">Edit</button>
                            <button onClick={() => handleDeleteAddress(addr.id)} className="text-xs font-bold uppercase tracking-widest text-red-400 hover:text-red-600">Delete</button>
                        </div>
                    </div>
                ))}
                {addresses.length === 0 && (
                    <div className="col-span-full text-center py-12 bg-gray-50 text-gray-400 text-sm uppercase tracking-widest border border-dashed border-gray-200">
                        No addresses found
                    </div>
                )}
            </div>

            {/* Address Modal */}
            {showAddressModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white p-8 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="font-serif text-2xl mb-6">{addressForm.id ? 'Edit Address' : 'Add New Address'}</h3>
                        <form onSubmit={handleAddressSubmit} className="space-y-6">
                            <div className="space-y-3 bg-white border border-gray-200 p-4 rounded-lg mb-6">
                                <div className="flex flex-col sm:flex-row gap-3 items-end">
                                    <div className="flex-1 w-full">
                                        <InputField placeholder="GhanaPostGPS (Optional)" value={addressForm.gps_address || ''} onChange={e => setAddressForm({ ...addressForm, gps_address: e.target.value })} />
                                    </div>
                                    <Button type="button" onClick={fetchDetailsFromGps} disabled={fetchingGps} variant="outline" className="w-full sm:w-auto shrink-0 mb-1 border-black text-black hover:bg-black hover:text-white">
                                        {fetchingGps ? 'Loading...' : 'Verify'}
                                    </Button>
                                </div>
                                <div className="relative py-2 flex items-center">
                                    <div className="flex-grow border-t border-gray-200"></div>
                                    <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase tracking-widest">or</span>
                                    <div className="flex-grow border-t border-gray-200"></div>
                                </div>
                                <Button type="button" onClick={handleUseCurrentLocation} disabled={fetchingGps} className="w-full justify-center text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 border-none transition-colors">
                                    <MapPin size={16} className="mr-2" /> Use My Current Location
                                </Button>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <InputField placeholder="Contact Name" value={addressForm.contact_name} onChange={e => setAddressForm({ ...addressForm, contact_name: e.target.value })} required />
                                <InputField placeholder="Contact Phone" value={addressForm.contact_phone} onChange={e => setAddressForm({ ...addressForm, contact_phone: e.target.value })} required />
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <InputField placeholder="Street Address" value={addressForm.street_address} onChange={e => setAddressForm({ ...addressForm, street_address: e.target.value })} required />
                                <InputField placeholder="Area" value={addressForm.area || ''} onChange={e => setAddressForm({ ...addressForm, area: e.target.value })} required />
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <InputField placeholder="City" value={addressForm.city} onChange={e => setAddressForm({ ...addressForm, city: e.target.value })} required />
                                <InputField placeholder="State/Region" value={addressForm.state} onChange={e => setAddressForm({ ...addressForm, state: e.target.value })} required />
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <InputField placeholder="Postal Code" value={addressForm.postal_code} onChange={e => setAddressForm({ ...addressForm, postal_code: e.target.value })} />
                                <InputField placeholder="Country" value={addressForm.country} onChange={e => setAddressForm({ ...addressForm, country: e.target.value })} required />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <Button type="button" variant="secondary" onClick={() => setShowAddressModal(false)} className="flex-1">Cancel</Button>
                                <Button type="submit" className="flex-1">Save Address</Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            toast.error("Passwords do not match");
            return;
        }
        try {
            await UserService.changePassword({ old_password: passwordForm.old_password, new_password: passwordForm.new_password });
            toast.success("Password changed successfully");
            setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to change password");
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            await NotificationService.markAsRead(id);
            loadData();
        } catch(err) {}
    };

    const handleMarkAllAsRead = async () => {
        try {
            await NotificationService.markAllAsRead();
            loadData();
        } catch(err) {}
    };

    const renderSecurityTab = () => (
        <div className="max-w-xl">
            <h3 className="font-serif text-2xl mb-8">Security</h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <InputField type="password" label="Current Password" value={passwordForm.old_password} onChange={e => setPasswordForm({...passwordForm, old_password: e.target.value})} required />
                <InputField type="password" label="New Password" value={passwordForm.new_password} onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})} required minLength={8} />
                <InputField type="password" label="Confirm New Password" value={passwordForm.confirm_password} onChange={e => setPasswordForm({...passwordForm, confirm_password: e.target.value})} required minLength={8} />
                <Button type="submit">
                    Update Password
                </Button>
            </form>
        </div>
    );

    const renderNotificationsTab = () => (
        <div className="max-w-3xl">
            <div className="flex justify-between items-center mb-8">
                <h3 className="font-serif text-2xl">Notifications</h3>
                {notifications.some(n => !n.is_read) && (
                    <Button onClick={handleMarkAllAsRead} variant="outline" className="text-xs border-black text-black hover:bg-black hover:text-white">
                        Mark All Read
                    </Button>
                )}
            </div>
            <div className="space-y-4">
                {notifications.map(n => (
                    <div key={n.id} onClick={() => !n.is_read && handleMarkAsRead(n.id)} className={`p-6 border relative transition-all ${n.is_read ? 'border-gray-200 bg-white' : 'border-black bg-gray-50 cursor-pointer hover:shadow-md'}`}>
                        {!n.is_read && <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-black"></span>}
                        <h4 className="font-bold text-sm uppercase tracking-wider mb-2">{n.title}</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{n.body}</p>
                    </div>
                ))}
                {notifications.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 text-gray-400 text-sm uppercase tracking-widest border border-dashed border-gray-200">
                        No notifications found
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto py-12 md:py-20 px-6">
            <h1 className="font-serif text-4xl mb-12">Account Settings</h1>

            <div className="flex flex-col md:flex-row gap-12">
                {/* Sidebar */}
                <div className="w-full md:w-64 flex-shrink-0">
                    <nav className="space-y-1">
                        <button
                            onClick={() => setActiveTab('profile')}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest transition-colors border-l-2 ${activeTab === 'profile' ? 'border-black font-bold bg-gray-50' : 'border-transparent text-gray-500 hover:text-black'}`}
                        >
                            <User size={16} /> Profile
                        </button>
                        <button
                            onClick={() => setActiveTab('addresses')}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest transition-colors border-l-2 ${activeTab === 'addresses' ? 'border-black font-bold bg-gray-50' : 'border-transparent text-gray-500 hover:text-black'}`}
                        >
                            <MapPin size={16} /> Addresses
                        </button>
                        <button
                            onClick={() => setActiveTab('security')}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest transition-colors border-l-2 ${activeTab === 'security' ? 'border-black font-bold bg-gray-50' : 'border-transparent text-gray-500 hover:text-black'}`}
                        >
                            <Shield size={16} /> Security
                        </button>
                        <button
                            onClick={() => setActiveTab('notifications')}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest transition-colors border-l-2 ${activeTab === 'notifications' ? 'border-black font-bold bg-gray-50' : 'border-transparent text-gray-500 hover:text-black'}`}
                        >
                            <Bell size={16} /> Notifications
                        </button>
                    </nav>
                </div>

                {/* Content */}
                <div className="flex-1 min-h-[500px]">
                    {loading ? (
                        <div className="text-center py-20 text-gray-400 text-sm uppercase tracking-widest">Loading...</div>
                    ) : (
                        <>
                            {activeTab === 'profile' && renderProfileTab()}
                            {activeTab === 'addresses' && renderAddressesTab()}
                            {activeTab === 'security' && renderSecurityTab()}
                            {activeTab === 'notifications' && renderNotificationsTab()}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserProfilePage;
